import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { formatDateLongFR } from "../lib/utils";
import { toast } from 'sonner';
import { PlanningCalendarSkeleton } from "./ui/Skeleton";
import { useCachedData } from "../hooks/useCachedData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlanningCalendar = () => {
  const [employees, setEmployees] = useState([]);
  const [housings, setHousings] = useState([]);
  const [clients, setClients] = useState([]);
  const [currentDate, setCurrentDate] = useState(() => {
    return new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Paris' }));
  });
  const [viewMode, setViewMode] = useState('month'); // 'month', 'week', 'day'
  const [selectedMission, setSelectedMission] = useState(null);
  const [editingMission, setEditingMission] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [syncingIcal, setSyncingIcal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    employee_id: "",
    date_mission: "",
    heure_debut: "",
    heure_fin: "",
    notes: "",
    prix: "",
    prix_intervenant: "",
    status: "planifie",
    en_binome: false,
    binome_employee_id: "",
    prix_binome: ""
  });

  // Fonction de fetch pour les missions du mois affiché
  const fetchMissions = useCallback(async () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const lastDayDate = new Date(year, month + 1, 0);
    const lastDay = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDayDate.getDate()).padStart(2, '0')}`;
    const response = await axios.get(`${API}/missions?start_date=${firstDay}&end_date=${lastDay}`);
    return response.data;
  }, [currentDate]);

  // Cache des missions avec stale-while-revalidate
  const {
    data: missions = [],
    loading,
    isRevalidating,
    refresh: refreshMissions,
    mutate: mutateMissions
  } = useCachedData('planning_missions', fetchMissions, {
    maxAge: 2 * 60 * 1000, // 2 minutes
    revalidateOnFocus: true,
    revalidateOnReconnect: true
  });

  // Flag pour empêcher le refresh silencieux pendant une action utilisateur
  const isUserActionRef = useRef(false);

  // Refresh silencieux en arrière-plan (30 secondes)
  useEffect(() => {
    const SILENT_REFRESH_INTERVAL = 30000;
    const interval = setInterval(() => {
      if (!isUserActionRef.current) {
        refreshMissions();
      }
    }, SILENT_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [refreshMissions]);

  // Charger les données de référence (employees, housings, clients) - ces données changent rarement
  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const [employeesRes, housingsRes, clientsRes] = await Promise.all([
          axios.get(`${API}/employees`),
          axios.get(`${API}/housings`),
          axios.get(`${API}/clients`)
        ]);
        setEmployees(employeesRes.data);
        setHousings(housingsRes.data);
        setClients(clientsRes.data);
      } catch (error) {
        console.error("Erreur lors du chargement des données de référence:", error);
      }
    };
    loadReferenceData();
  }, []);

  // Synchronisation iCal
  const syncIcal = async () => {
    setSyncingIcal(true);
    try {
      const response = await axios.post(`${API}/ical/sync-all`);
      const data = response.data;
      toast.success(`✅ Synchro iCal terminée : ${data.new_reservations || 0} nouvelle(s) réservation(s)`);
      // Recharger les données
      refreshMissions();
    } catch (error) {
      toast.error('❌ Erreur synchro iCal');
    } finally {
      setSyncingIcal(false);
    }
  };

  const handleEditMission = (missionData) => {
    setEditingMission(missionData);
    const mission = missionData.mission;
    setEditFormData({
      employee_id: mission.employee_id || "",
      date_mission: mission.date_mission,
      heure_debut: mission.heure_debut,
      heure_fin: mission.heure_fin,
      notes: mission.notes || "",
      prix: mission.prix ? mission.prix.toString() : "",
      prix_intervenant: mission.prix_intervenant ? mission.prix_intervenant.toString() : "",
      status: mission.status,
      en_binome: mission.en_binome || false,
      binome_employee_id: mission.binome_employee_id || "",
      prix_binome: mission.prix_binome ? mission.prix_binome.toString() : ""
    });
    setSelectedMission(null); // Fermer le modal de détails
    setShowEditModal(true);
  };

  const handleUpdateMission = async (e) => {
    e.preventDefault();
    isUserActionRef.current = true;

    const updateData = {
      ...editFormData,
      prix: editFormData.prix ? parseFloat(editFormData.prix) : null,
      prix_intervenant: editFormData.prix_intervenant ? parseFloat(editFormData.prix_intervenant) : null,
      en_binome: editFormData.en_binome || false,
      binome_employee_id: editFormData.en_binome ? (editFormData.binome_employee_id || null) : null,
      prix_binome: editFormData.en_binome && editFormData.prix_binome ? parseFloat(editFormData.prix_binome) : null
    };

    // Sauvegarder pour rollback
    const previousMissions = missions;
    const missionId = editingMission.mission.id;

    // Mise à jour optimiste locale
    mutateMissions(prev => prev.map(m => {
      if (m.mission.id === missionId) {
        return {
          ...m,
          mission: { ...m.mission, ...updateData },
          employee: updateData.employee_id
            ? employees.find(emp => emp.id === updateData.employee_id) || m.employee
            : null
        };
      }
      return m;
    }));

    // Fermer le modal immédiatement
    setShowEditModal(false);
    setEditingMission(null);
    toast.success("Mission modifiée");

    // Appel API en arrière-plan
    try {
      await axios.put(`${API}/missions/${missionId}`, updateData);
    } catch (error) {
      console.error("Erreur lors de la modification:", error);
      if (!error.moveCancelled) toast.error("Erreur serveur — modification annulée");
      mutateMissions(previousMissions);
    } finally {
      isUserActionRef.current = false;
    }
  };

  const handleDeleteMission = async (missionData) => {
    isUserActionRef.current = true;
    const missionId = missionData.mission.id;

    // Sauvegarder pour rollback
    const previousMissions = missions;

    // Suppression optimiste locale
    mutateMissions(prev => prev.filter(m => m.mission.id !== missionId));

    // Fermer le modal immédiatement
    setSelectedMission(null);
    toast.success("Mission supprimée");

    // Appel API en arrière-plan
    try {
      await axios.delete(`${API}/missions/${missionId}`);
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      const errorMessage = error.response?.data?.detail || "Erreur serveur — suppression annulée";
      toast.error(errorMessage);
      mutateMissions(previousMissions);
    } finally {
      isUserActionRef.current = false;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'planifie': 'bg-blue-100 text-blue-800 border-blue-200',
      'en_cours': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'termine': 'bg-green-100 text-green-800 border-green-200',
      'annule': 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getMonthDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Convertir getDay() (0=Dimanche) en format européen (0=Lundi, 6=Dimanche)
    let startingDayOfWeek = firstDay.getDay();
    startingDayOfWeek = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
    
    const days = [];
    
    // Jours du mois précédent
    const prevMonth = new Date(year, month - 1, 0);
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonth.getDate() - i),
        isCurrentMonth: false
      });
    }
    
    // Jours du mois actuel
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        date: new Date(year, month, day),
        isCurrentMonth: true
      });
    }
    
    // Jours du mois suivant pour compléter la grille
    const totalCells = Math.ceil(days.length / 7) * 7;
    let nextMonthDay = 1;
    for (let i = days.length; i < totalCells; i++) {
      days.push({
        date: new Date(year, month + 1, nextMonthDay),
        isCurrentMonth: false
      });
      nextMonthDay++;
    }
    
    return days;
  };

  const getMissionsForDate = (date) => {
    const dateString = date.toISOString().split('T')[0];
    return missions.filter(missionData => {
      if (missionData.mission.date_mission !== dateString) return false;
      if (missionData.mission.status === 'annule') return false;
      
      return true;
    });
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const navigateToToday = () => {
    setCurrentDate(new Date());
  };

  const formatMissionTime = (mission) => {
    return `${mission.heure_debut} - ${mission.heure_fin}`;
  };

  const getEmployeeName = (mission) => {
    if (!mission.employee) return "Non assignée";
    return `${mission.employee.prenom} ${mission.employee.nom}`;
  };

  const monthNames = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
  ];

  const dayNames = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-6 px-4" data-testid="planning-calendar-loading">
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">📅 Planning</h1>
              <p className="text-gray-600 mt-2">Chargement...</p>
            </div>
          </div>
        </div>
        <PlanningCalendarSkeleton />
      </div>
    );
  }

  const monthDays = getMonthDays(currentDate);

  return (
    <div className="max-w-7xl mx-auto py-6 px-4" data-testid="planning-calendar">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">📅 Planning</h1>
            <p className="text-gray-600 mt-2">
              Vue calendrier des missions planifiées
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {/* Indicateur de revalidation */}
            {isRevalidating && (
              <span className="text-xs text-blue-600 flex items-center gap-1 animate-pulse">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                Mise à jour...
              </span>
            )}
            <button
              onClick={syncIcal}
              disabled={syncingIcal}
              className={`btn-secondary ${syncingIcal ? 'opacity-50' : ''}`}
              data-testid="sync-ical-btn"
              title="Synchroniser les calendriers iCal (Airbnb, Booking...)"
            >
              {syncingIcal ? '🔄 Synchro...' : '🔄 Sync iCal'}
            </button>
            <button
              onClick={navigateToToday}
              className="btn-secondary"
              data-testid="today-btn"
            >
              📍 Aujourd'hui
            </button>
            <a
              href="/missions"
              className="btn-primary"
              data-testid="manage-missions-btn"
            >
              🎯 Gérer Missions
            </a>
          </div>
        </div>
      </div>

      {/* Navigation du calendrier */}
      <div className="card mb-6">
        <div className="card-body">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-2 rounded-md hover:bg-gray-100"
                data-testid="prev-month-btn"
              >
                ← Précédent
              </button>
              <h2 className="text-xl font-semibold text-gray-900">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <button
                onClick={() => navigateMonth(1)}
                className="p-2 rounded-md hover:bg-gray-100"
                data-testid="next-month-btn"
              >
                Suivant →
              </button>
            </div>
            
            <div className="flex space-x-2 text-sm">
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-blue-100 border border-blue-200 rounded"></div>
                <span>Planifiée</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-yellow-100 border border-yellow-200 rounded"></div>
                <span>En cours</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-green-100 border border-green-200 rounded"></div>
                <span>Terminée</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-red-100 border border-red-200 rounded"></div>
                <span>Annulée</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Calendrier */}
      <div className="card">
        <div className="card-body p-0">
          {/* En-têtes des jours */}
          <div className="grid grid-cols-7 bg-gray-50">
            {dayNames.map((day, index) => (
              <div key={index} className="p-4 text-center text-sm font-medium text-gray-700 border-r border-gray-200 last:border-r-0">
                {day}
              </div>
            ))}
          </div>
          
          {/* Grille du calendrier */}
          <div className="calendar-grid">
            {monthDays.map((dayData, index) => {
              const dayMissions = getMissionsForDate(dayData.date);
              const isToday = dayData.date.toDateString() === new Date().toDateString();
              
              return (
                <div
                  key={index}
                  className={`calendar-day min-h-32 p-2 border-r border-b border-gray-200 ${
                    !dayData.isCurrentMonth ? 'calendar-day other-month' : ''
                  } ${isToday ? 'calendar-day today' : ''}`}
                  data-testid={`calendar-day-${dayData.date.toISOString().split('T')[0]}`}
                >
                  <div className="font-medium text-sm mb-1">
                    {dayData.date.getDate()}
                  </div>
                  
                  <div className="space-y-1">
                    {dayMissions.slice(0, 3).map((missionData) => {
                      const mission = missionData.mission;
                      const employee = missionData.employee;
                      const housing = missionData.housing;
                      
                      // Bordure colorée selon le propriétaire du logement
                      const ownerBorderClass = housing?.appartient_a === 'cyril' 
                        ? 'border-l-4 border-l-blue-500' 
                        : housing?.appartient_a === 'ben' 
                          ? 'border-l-4 border-l-violet-500' 
                          : '';
                      
                      return (
                        <div
                          key={mission.id}
                          className={`text-xs p-1 rounded border cursor-pointer hover:opacity-80 ${getStatusColor(mission.status)} ${ownerBorderClass}`}
                          onClick={() => setSelectedMission(missionData)}
                          data-testid={`calendar-mission-${mission.id}`}
                        >
                          <div className="font-medium truncate">
                            {formatMissionTime(mission)}
                          </div>
                          <div className="truncate">
                            {employee ? `${employee.prenom} ${employee.nom}` : "Non assignée"}
                          </div>
                          {missionData.client && (
                            <div className="truncate text-gray-600">
                              {missionData.client.prenom} {missionData.client.nom}
                            </div>
                          )}
                          {missionData.housing?.pseudo && (
                            <div className="flex items-center gap-1 truncate text-xs font-medium">
                              <span className="text-purple-600 truncate flex items-center gap-1">
                                🎤 {missionData.housing.pseudo}
                                {missionData.mission.source === 'ical_airbnb' && (
                                  <span 
                                    className="inline-flex items-center px-1 rounded text-[10px] font-bold bg-red-100 text-red-700"
                                    title="Airbnb"
                                  >
                                    Airbnb
                                  </span>
                                )}
                                {missionData.mission.source === 'ical_booking' && (
                                  <span 
                                    className="inline-flex items-center px-1 rounded text-[10px] font-bold bg-blue-100 text-blue-700"
                                    title="Booking"
                                  >
                                    Booking
                                  </span>
                                )}
                                {missionData.mission.en_binome && (
                                  <span 
                                    className="inline-flex items-center px-1 rounded text-[10px] font-bold bg-amber-100 text-amber-700"
                                    title="En binôme"
                                  >
                                    👥
                                  </span>
                                )}
                              </span>
                            </div>
                          )}
                          {!missionData.housing && mission.notes && (
                            <div className="truncate text-red-600 text-xs font-medium">
                              📅 {mission.notes}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    
                    {dayMissions.length > 3 && (
                      <div className="text-xs text-gray-500 text-center">
                        +{dayMissions.length - 3} autres
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Résumé du mois */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="card-body text-center">
            <div className="text-2xl font-bold text-blue-600">
              {missions.filter(m => {
                const missionDate = new Date(m.mission.date_mission);
                return missionDate.getMonth() === currentDate.getMonth() && 
                       missionDate.getFullYear() === currentDate.getFullYear();
              }).length}
            </div>
            <div className="text-sm text-gray-600">Missions ce mois</div>
          </div>
        </div>
        
        <div className="card">
          <div className="card-body text-center">
            <div className="text-2xl font-bold text-green-600">
              {missions.filter(m => {
                const missionDate = new Date(m.mission.date_mission);
                return missionDate.getMonth() === currentDate.getMonth() && 
                       missionDate.getFullYear() === currentDate.getFullYear() &&
                       m.mission.status === 'termine';
              }).length}
            </div>
            <div className="text-sm text-gray-600">Terminées</div>
          </div>
        </div>
        
        <div className="card">
          <div className="card-body text-center">
            <div className="text-2xl font-bold text-yellow-600">
              {missions.filter(m => {
                const missionDate = new Date(m.mission.date_mission);
                return missionDate.getMonth() === currentDate.getMonth() && 
                       missionDate.getFullYear() === currentDate.getFullYear() &&
                       m.mission.status === 'planifie';
              }).length}
            </div>
            <div className="text-sm text-gray-600">Planifiées</div>
          </div>
        </div>
        
        <div className="card">
          <div className="card-body text-center">
            <div className="text-2xl font-bold text-purple-600">
              {missions.filter(m => {
                const missionDate = new Date(m.mission.date_mission);
                return missionDate.getMonth() === currentDate.getMonth() && 
                       missionDate.getFullYear() === currentDate.getFullYear() &&
                       !m.mission.employee_id;
              }).length}
            </div>
            <div className="text-sm text-gray-600">Non assignées</div>
          </div>
        </div>
      </div>

      {/* Modal détails de mission */}
      {selectedMission && (
        <div className="modal-overlay" data-testid="mission-details-modal">
          <div className="modal-content">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Détails de la Mission
              </h3>
              <button
                onClick={() => setSelectedMission(null)}
                className="text-gray-400 hover:text-gray-600"
                data-testid="close-mission-details"
              >
                ❌
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Date et horaire</label>
                <p className="mt-1 text-sm text-gray-900">
                  {formatDateLongFR(new Date(selectedMission.mission.date_mission))} de {formatMissionTime(selectedMission.mission)}
                </p>
              </div>
              
              {selectedMission.client && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Client</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedMission.client.prenom} {selectedMission.client.nom}
                  </p>
                </div>
              )}
              
              {selectedMission.housing?.pseudo && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Pseudo vocal</label>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-sm text-purple-600 font-medium flex items-center gap-2">
                      🎤 {selectedMission.housing.pseudo}
                      {selectedMission.mission.source === 'ical_airbnb' && (
                        <span 
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-700"
                          title="Airbnb"
                        >
                          Airbnb
                        </span>
                      )}
                      {selectedMission.mission.source === 'ical_booking' && (
                        <span 
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-700"
                          title="Booking"
                        >
                          Booking
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              )}
              
              {selectedMission.housing && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Adresse</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedMission.housing.adresse}, {selectedMission.housing.ville} {selectedMission.housing.code_postal}
                  </p>
                </div>
              )}
              
              {/* Instructions spéciales */}
              {selectedMission.housing?.instructions && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">🔑 Instructions spéciales</label>
                  <p className="mt-1 text-sm text-red-700 whitespace-pre-wrap">
                    {selectedMission.housing.instructions}
                  </p>
                </div>
              )}
              
              {/* Lien vers la fiche ménage Google Docs */}
              {selectedMission.housing?.lien_fiche_menage && (
                <div className="bg-green-50 p-3 rounded-lg border border-green-300">
                  <label className="block text-sm font-medium text-green-700 mb-1">📄 Fiche d'instructions ménage</label>
                  <a
                    href={selectedMission.housing.lien_fiche_menage}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm text-green-600 hover:text-green-800 underline font-medium"
                  >
                    📎 Ouvrir la fiche ménage
                  </a>
                </div>
              )}

              {!selectedMission.housing && selectedMission.mission.notes && !selectedMission.mission.notes.startsWith('🌀 Créé automatiquement') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <p className="mt-1 text-sm text-red-700 font-semibold">
                    📅 DIVERS : {selectedMission.mission.notes}
                  </p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Employée assignée</label>
                <p className="mt-1 text-sm text-gray-900">
                  {getEmployeeName(selectedMission)}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Statut</label>
                <div className="mt-1">
                  <span className={`badge badge-${selectedMission.mission.status === 'planifie' ? 'info' : 
                    selectedMission.mission.status === 'en_cours' ? 'warning' :
                    selectedMission.mission.status === 'termine' ? 'success' : 'danger'}`}>
                    {selectedMission.mission.status === 'planifie' ? 'Planifiée' :
                     selectedMission.mission.status === 'en_cours' ? 'En cours' :
                     selectedMission.mission.status === 'termine' ? 'Terminée' : 'Annulée'}
                  </span>
                </div>
              </div>
              
              {selectedMission.mission.prix && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Prix</label>
                  <p className="mt-1 text-sm font-medium text-green-600">
                    {selectedMission.mission.prix}€
                  </p>
                </div>
              )}
              
              {selectedMission.mission.notes && !selectedMission.mission.notes.startsWith('🌀 Créé automatiquement') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedMission.mission.notes}
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex justify-between pt-4 mt-6 border-t">
              <button
                onClick={() => handleDeleteMission(selectedMission)}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                🗑️ Supprimer
              </button>
              <div className="flex space-x-3">
                <button
                  onClick={() => handleEditMission(selectedMission)}
                  className="btn-primary"
                >
                  ✏️ Modifier
                </button>
                <button
                  onClick={() => setSelectedMission(null)}
                  className="btn-secondary"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'édition de mission */}
      {showEditModal && editingMission && (
        <div className="modal-overlay" data-testid="mission-edit-modal">
          <div className="modal-content max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                ✏️ Modifier la Mission
              </h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingMission(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ❌
              </button>
            </div>

            <form onSubmit={handleUpdateMission}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {editingMission.housing ? 'Logement' : 'Événement'}
                  </label>
                  <div className={`font-medium ${!editingMission.housing ? 'text-red-700' : 'text-gray-900'}`}>
                    {editingMission.housing 
                      ? (editingMission.housing.pseudo || editingMission.housing.adresse)
                      : `📅 DIVERS : ${editingMission.mission.notes || ''}`
                    }
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date *
                    </label>
                    <input
                      type="date"
                      required
                      value={editFormData.date_mission}
                      onChange={(e) => setEditFormData({...editFormData, date_mission: e.target.value})}
                      className="form-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Employée
                    </label>
                    <select
                      value={editFormData.employee_id}
                      onChange={(e) => setEditFormData({...editFormData, employee_id: e.target.value})}
                      className="form-select"
                    >
                      <option value="">Non assignée</option>
                      {employees.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.prenom} {emp.nom}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Binôme */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editFormData.en_binome}
                      onChange={(e) => setEditFormData({...editFormData, en_binome: e.target.checked, binome_employee_id: e.target.checked ? editFormData.binome_employee_id : ""})}
                      className="rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                      data-testid="binome-checkbox"
                    />
                    <span className="text-sm font-medium text-amber-900">👥 En binôme (formation)</span>
                    {editFormData.en_binome && <span className="text-xs text-amber-700 ml-auto">Prix intervenant ÷ 2</span>}
                  </label>
                  {editFormData.en_binome && (
                    <div className="mt-2 space-y-2">
                      <select
                        value={editFormData.binome_employee_id}
                        onChange={(e) => setEditFormData({...editFormData, binome_employee_id: e.target.value})}
                        className="form-select text-sm"
                        data-testid="binome-employee-select"
                      >
                        <option value="">-- Choisir le binôme --</option>
                        {employees.filter(emp => emp.id !== editFormData.employee_id).map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {emp.prenom} {emp.nom} {emp.type_employe === 'proprietaire' ? '(Gérant)' : ''}
                          </option>
                        ))}
                      </select>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs text-amber-800 mb-0.5">Prix employé principal (€)</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={editFormData.prix_intervenant}
                            onChange={(e) => setEditFormData({...editFormData, prix_intervenant: e.target.value})}
                            className="form-input text-sm"
                            placeholder="Prix libre"
                            data-testid="binome-prix-principal"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-amber-800 mb-0.5">Prix binôme (€)</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={editFormData.prix_binome}
                            onChange={(e) => setEditFormData({...editFormData, prix_binome: e.target.value})}
                            className="form-input text-sm"
                            placeholder="Prix libre"
                            data-testid="binome-prix-binome"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Heure début *
                    </label>
                    <input
                      type="time"
                      required
                      value={editFormData.heure_debut}
                      onChange={(e) => setEditFormData({...editFormData, heure_debut: e.target.value})}
                      className="form-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Heure fin *
                    </label>
                    <input
                      type="time"
                      required
                      value={editFormData.heure_fin}
                      onChange={(e) => setEditFormData({...editFormData, heure_fin: e.target.value})}
                      className="form-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Statut *
                  </label>
                  <select
                    required
                    value={editFormData.status}
                    onChange={(e) => setEditFormData({...editFormData, status: e.target.value})}
                    className="form-select"
                  >
                    <option value="planifie">Planifiée</option>
                    <option value="en_cours">En cours</option>
                    <option value="termine">Terminée</option>
                    <option value="annule">Annulée</option>
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prix M&M Conciergerie (€)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editFormData.prix}
                      onChange={(e) => setEditFormData({...editFormData, prix: e.target.value})}
                      className="form-input"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prix Intervenant (€)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editFormData.prix_intervenant}
                      onChange={(e) => setEditFormData({...editFormData, prix_intervenant: e.target.value})}
                      className="form-input"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={editFormData.notes}
                    onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                    rows={3}
                    className="form-input"
                    placeholder="Notes additionnelles..."
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 mt-6 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingMission(null);
                  }}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  ✅ Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanningCalendar;